import { ButtonGroup, Divider, HStack, Text, useColorMode } from '@chakra-ui/react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { Body, IconButton } from '@missionlabs/react'
import { ChatIconSolid, SendIconSolid, SmileEmojiPlusIconRegular } from '@missionlabs/react/zeta'
import { TextContentListener } from 'lexical/LexicalEditor'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TOGGLE_EMOJI_PICKER_COMMAND } from 'shared/components/Lexical/plugins/EmojiPickerPlugin'

import {
    SEND_MESSAGE_COMMAND,
    SMS_CHARACTER_LIMIT,
    SMS_MAX_MESSAGE_LENGTH
} from './SMSSendMessagePlugin'

interface SMSMessageToolbarProps {
    limitExceeded?: boolean
}

export const SMSMessageToolbar: FC<SMSMessageToolbarProps> = ({ limitExceeded }) => {
    const { t } = useTranslation()
    const { colorMode } = useColorMode()
    const [editor] = useLexicalComposerContext()
    const [canSend, setCanSend] = useState(false)

    const [totalCharCount, setTotalCharacterCount] = useState(0)
    const charCount = totalCharCount % SMS_MAX_MESSAGE_LENGTH
    const messageCount = Math.ceil(totalCharCount / SMS_MAX_MESSAGE_LENGTH)

    useEffect(() => {
        const handleContentChange: TextContentListener = content => {
            setTotalCharacterCount(content.length)
            setCanSend(content.length > 0)
        }
        const teardown = editor.registerTextContentListener(handleContentChange)
        return () => teardown()
    }, [editor, setTotalCharacterCount, setCanSend])

    const renderCharCount = () => {
        if (totalCharCount >= SMS_CHARACTER_LIMIT) {
            return (
                <Body size="sm" variant="bold" as="span" color={`${colorMode}.alerts.red`}>
                    Character Limit Reached
                </Body>
            )
        }
        return `${charCount} / ${SMS_MAX_MESSAGE_LENGTH}`
    }

    return (
        <HStack
            spacing={0}
            py="8px"
            w="full"
            h="full"
            justify="space-between"
            data-testid="chat-toolbar"
        >
            <HStack spacing="8px">
                <ButtonGroup spacing={0}>
                    <IconButton
                        size="sm"
                        variant="ghost"
                        aria-label="Add emoji"
                        onClick={() => {
                            editor.dispatchCommand(TOGGLE_EMOJI_PICKER_COMMAND, undefined)
                        }}
                    >
                        <SmileEmojiPlusIconRegular boxSize="24px" p="4px" />
                    </IconButton>
                </ButtonGroup>
                <Divider orientation="vertical" h="32px" />
                {limitExceeded && (
                    <Text fontSize="sm" color={'red.500'}>
                        {t('sms.limitError')}
                    </Text>
                )}
            </HStack>
            <HStack justify="end" gap="8px" alignItems="center">
                <Text fontSize="xs">{renderCharCount()}</Text>
                <HStack alignItems="center" gap="1px">
                    <ChatIconSolid boxSize="24px" p="4px" />
                    <Text fontSize="xs">{messageCount}</Text>
                </HStack>

                <IconButton
                    size="sm"
                    variant="ghost"
                    isActive={canSend}
                    isDisabled={!canSend}
                    aria-label="Send message"
                    onClick={() => editor.dispatchCommand(SEND_MESSAGE_COMMAND, undefined)}
                    pl="4px"
                >
                    <SendIconSolid boxSize="24px" p="4px" />
                </IconButton>
            </HStack>
        </HStack>
    )
}
